/**
 * TASK-833 — the two urgency axes and the breach age, in ONE implementation.
 *
 * TASK-821 put the mandatory band and `sla_class` beside each other, and TASK-822 made the
 * age the age of the *breach* rather than of the alert row. Both landed inline in the Radar
 * row. The dossier renders the same three facts, and a second copy of a rule this delicate —
 * "which age is this, and what is it the age of" — is exactly the shape of defect this epic
 * keeps finding: `CAUSE_COPY` was a second copy of the label catalogue, and `DNA_TAB_CAUSES`
 * a second copy of the destination map.
 *
 * So the badges moved here and the Radar row mounts this. Nothing about them changed; the
 * reasoning is preserved verbatim below because it is the part that is easy to lose.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';

/** Only the fields these badges read — so a dossier's subject and a Radar row both fit. */
export interface SignalUrgencyFacts {
  event_ts?: string | null;
  due_at?: string | null;
  sla_state?: string | null;
  sla_class?: string | null;
  mandatory?: boolean;
}

export const timeAgo = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
};

/**
 * TASK-822: WHICH age a row should show, and what it is the age OF.
 *
 * `timeAgo(e.event_ts)` is the age of the ALERT ROW — `radar_service` sets `event_ts` from
 * `alerts.created_at`. Live on 2026-08-25 a re-profile whose review was due 2025-06-20 —
 * 431 days, 14.2 months — rendered as `28d`, because the detector had emitted the alert 28
 * days earlier. That understated a mandatory regulatory lapse by a factor of 15, on the one
 * signal class where the firm has no discretion.
 *
 * Where the emitter supplied a due date, the BREACH's age is the number that matters and
 * the detection age is supporting detail. Where it did not, the number shown is the
 * detection age and it is labelled as such — never one silently standing in for the other.
 */
export const signalAge = (e: SignalUrgencyFacts) => {
  const detected = timeAgo(e.event_ts);
  if (e.due_at && e.sla_state === 'breached_derived') {
    return {
      text: timeAgo(e.due_at),
      title: `Overdue for this long. Detected ${detected} ago.`,
      isBreach: true,
    };
  }
  return { text: detected, title: `Detected ${detected} ago`, isBreach: false };
};

/**
 * The urgency badges, in the order the Radar row has always rendered them.
 *
 * `showAge` exists because the two surfaces disagree about ONE thing: the Radar row prefixes
 * the age with "·" inside a flowing line of metadata, while the dossier header states it on
 * its own. Everything else — which badge, which colour, which title — is identical and must
 * stay identical.
 */
export const SignalUrgency: React.FC<{
  facts: SignalUrgencyFacts;
  showAge?: boolean;
  agePrefix?: string;
}> = ({ facts, showAge = true, agePrefix = '· ' }) => {
  const { t } = useTranslation('dashboard');
  const age = signalAge(facts);
  return (
    <>
      {/* TASK-821: urgency INSIDE the mandatory band. `sla_class` is what `get_radar`
          sorts on and it was rendered nowhere — so the band's own ordering was
          invisible while advisors read the red badge as "most urgent". */}
      {facts.mandatory && facts.sla_class && (
        <span
          data-testid="radar-sla-class"
          title={t('radar.slaClassHint', 'How urgent this obligation is within the mandatory band.')}
          className="rounded bg-gray-100 px-1 py-0.5 text-[9px] font-medium uppercase text-gray-600"
        >
          {facts.sla_class.replace(/_/g, ' ')}
        </span>
      )}
      {facts.sla_state === 'undated' && (
        // TASK-822: an obligation with no deadline at all — `reprofile_due` fired for
        // an incomplete profile, whose branch returns before a due date is parsed.
        // Neither breached nor on track: it has no track. Rendering it as "not
        // breached" put a live suitability gap below every dated row.
        <span
          data-testid="radar-sla-undated"
          className="rounded bg-amber-100 px-1 py-0.5 text-[9px] font-semibold uppercase text-amber-800"
        >
          {t('radar.slaUndated', 'No due date set')}
        </span>
      )}
      {facts.sla_state === 'breached_derived' && (
        // Deliberately NOT the same badge as a by-nature breach. This one was INFERRED
        // from a date an emitter supplied, so it names that date — that is the evidence
        // a reader would want to check, and it is only as sound as the date behind it.
        <span
          data-testid="radar-sla-breached-derived"
          className="rounded bg-red-100 px-1 py-0.5 text-[9px] font-semibold uppercase text-red-800"
          title={facts.due_at ? `Due ${facts.due_at.slice(0, 10)}` : undefined}
        >
          {facts.due_at
            ? t('radar.slaOverdueSince', {
                date: facts.due_at.slice(0, 10),
                defaultValue: `Overdue since ${facts.due_at.slice(0, 10)}`,
              })
            : t('radar.slaBreached', 'SLA breached')}
        </span>
      )}
      {facts.sla_state === 'breached_by_nature' && (
        <span data-testid="radar-sla-breached" className="font-semibold text-red-600">
          {t('radar.slaBreached', 'SLA breached')}
        </span>
      )}
      {showAge && facts.event_ts && (
        <span
          data-testid={age.isBreach ? 'radar-age-breach' : 'radar-age-detected'}
          title={age.title}
          className={age.isBreach ? 'font-semibold text-red-700' : undefined}
        >
          {agePrefix}
          {age.text}
          {age.isBreach && (
            <span className="ml-0.5 font-normal text-gray-400">
              {t('radar.ageOverdue', 'overdue')}
            </span>
          )}
        </span>
      )}
    </>
  );
};

export default SignalUrgency;
